/**
 * Trend Booster Strategy — faithful port of the AmiBroker AFL source.
 *
 * Combines two sub-indicators that must agree for a valid signal:
 *
 *   1. CCI-based Trend Magic — CCI on HLC/3; LONG when CCI crosses above 0,
 *      SHORT when it crosses below.  Forward-filled.
 *   2. Bollinger Bands Trailing Stop — BB on close; LONG when price breaks
 *      above upper band, SHORT below lower band.  Bands trail (never move
 *      against position).  Money-risk adjustment applied to stops.
 *
 * When both agree → new signal:
 *   Entry  = current close
 *   StopLoss = BB trailing stop level
 *   Target1 = entry ± 0.618 × risk   (Fibonacci)
 *   Target2 = entry ± 1.618 × risk
 *
 * Additional AFL features ported:
 *   - Buy/Sell arrow markers at signal transitions
 *   - Neutral state detection (BB ≠ TM)
 *   - Target / Stop Loss hit detection (HighestSince / LowestSince logic)
 *   - Live P/L and Booked P/L calculation
 *   - Dashboard metadata for overlay component
 *
 * Plots:
 *   - Entry line (white, dashed from last signal)
 *   - Stop Loss line (yellow)
 *   - Target 1 line (green)
 *   - Target 2 line (green)
 *   - BB trailing stop curve (orange = long / red = short)
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA } from "./sma";
import { inferTickSize, roundToTick } from "./price-utils";

// ─── Constants ───────────────────────────────────────────────────
const TS_NONE = 0;
const TS_LONG = 1;
const TS_SHORT = 2;
type TSignal = typeof TS_NONE | typeof TS_LONG | typeof TS_SHORT;

// ─── Bollinger Band helpers (no external lib) ────────────────────
function computeBBands(
  closes: number[],
  period: number,
  stdDevMult: number,
): { upper: number[]; lower: number[] } {
  const sma = computeSMA(closes, period);
  const upper: number[] = new Array(closes.length).fill(0);
  const lower: number[] = new Array(closes.length).fill(0);

  for (let i = period - 1; i < closes.length; i++) {
    const m = sma[i];
    if (m === undefined) continue;
    let sumSq = 0;
    for (let j = i - period + 1; j <= i; j++) {
      const d = closes[j] - m;
      sumSq += d * d;
    }
    const sd = Math.sqrt(sumSq / period);
    upper[i] = m + stdDevMult * sd;
    lower[i] = m - stdDevMult * sd;
  }
  return { upper, lower };
}

// ─── CCI computation (AmiBroker standard: HLC/3, mean deviation) ─
function computeCCI(candles: CandleData[], period: number): number[] {
  const hlc3 = candles.map(c => (c.high + c.low + c.close) / 3);
  const cci: number[] = new Array(candles.length).fill(0);

  for (let i = period - 1; i < candles.length; i++) {
    let sum = 0;
    for (let j = i - period + 1; j <= i; j++) sum += hlc3[j];
    const mean = sum / period;

    let devSum = 0;
    for (let j = i - period + 1; j <= i; j++) devSum += Math.abs(hlc3[j] - mean);
    const meanDev = devSum / period;

    cci[i] = meanDev !== 0 ? (hlc3[i] - mean) / (0.015 * meanDev) : 0;
  }
  return cci;
}

// ─── ATR computation (for arrow offset like AFL) ─────────────────
function computeATR(candles: CandleData[], period: number): number[] {
  const atr: number[] = new Array(candles.length).fill(0);
  if (candles.length < 2) return atr;

  // True Range
  const tr: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high;
    const l = candles[i].low;
    const pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }

  // Simple average for first period, then EMA-style
  let sum = 0;
  for (let i = 0; i < Math.min(period, tr.length); i++) sum += tr[i];
  atr[period - 1] = sum / period;
  for (let i = period; i < tr.length; i++) {
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  return atr;
}

// ─── Dashboard data interface ────────────────────────────────────
export interface AlphaTradeDashboard {
  signal: "BUY" | "SELL" | null;
  trend: "UP" | "DOWN" | "NEUTRAL";
  entry: number | null;
  stopLoss: number | null;
  target1: number | null;
  target2: number | null;
  currentPrice: number;
  livePL: number;
  bookedPL: number;
  status: "Active" | "Target1 Hit" | "Target2 Hit" | "Stop Loss Hit";
  tp1Hit: boolean;
  tp2Hit: boolean;
  slHit: boolean;
  candleState: "long" | "short" | "neutral";
}

export const alphaTradeIndicator: IndicatorDefinition = {
  id: "alpha_trade",
  name: "Trend Booster",
  shortName: "ALPHA",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "alertEntry", label: "Alert on Entry", type: "boolean", default: true },
    { key: "alertTP1", label: "Alert on TP1 Hit", type: "boolean", default: true },
    { key: "alertTP2", label: "Alert on TP2 Hit", type: "boolean", default: true },
    { key: "alertSL", label: "Alert on SL Hit", type: "boolean", default: true },
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#1E88E5", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#FF6D00", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target1", label: "Target 1", color: "#00C853", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target2", label: "Target 2", color: "#00C853", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const cciPeriod = 50;
    const bbPeriod = 10;
    const bbDeviation = 5;
    const moneyRisk = 1.0;
    const len = candles.length;
    const minBars = Math.max(cciPeriod, bbPeriod) + 2;

    const emptyResult: PlotData[] = [
      { key: "entry", data: [] },
      { key: "stopLoss", data: [] },
      { key: "target1", data: [] },
      { key: "target2", data: [] },
    ];

    if (len < minBars) return emptyResult;

    // ════════════════════════════════════════════════════════════════
    // 1. CCI-based Trend Magic (AFL: TM_SIGNAL with forward-fill)
    // ════════════════════════════════════════════════════════════════
    const cci = computeCCI(candles, cciPeriod);
    const tmSignal: TSignal[] = new Array(len).fill(TS_NONE);

    for (let i = 1; i < len; i++) {
      // Cross(CCI_Value, 0) = LONG; Cross(0, CCI_Value) = SHORT
      if (cci[i - 1] <= 0 && cci[i] > 0) tmSignal[i] = TS_LONG;
      else if (cci[i - 1] >= 0 && cci[i] < 0) tmSignal[i] = TS_SHORT;
      else tmSignal[i] = tmSignal[i - 1]; // forward-fill
    }

    // ════════════════════════════════════════════════════════════════
    // 2. Bollinger Bands Trailing Stop (matching MT4/AFL exactly)
    // ════════════════════════════════════════════════════════════════
    const closes = candles.map(c => c.close);
    const { upper: bbUpper, lower: bbLower } = computeBBands(closes, bbPeriod, bbDeviation);

    const bbSignal: TSignal[] = new Array(len).fill(TS_NONE);
    const bbStop: number[] = new Array(len).fill(0);

    let bbState: TSignal = TS_NONE;
    let prevBbU = bbUpper[0] || closes[0];
    let prevBbL = bbLower[0] || closes[0];
    let prevSlLong = prevBbL;
    let prevSlShort = prevBbU;

    for (let i = 1; i < len; i++) {
      const close = candles[i].close;

      // Price breaks determine signal — AFL logic
      if (close > prevBbU) bbState = TS_LONG;
      if (close < prevBbL) bbState = TS_SHORT;

      let curU = bbUpper[i] || prevBbU;
      let curL = bbLower[i] || prevBbL;

      // Trail bands — never move against position
      if (bbState === TS_LONG && curL < prevBbL) curL = prevBbL;
      if (bbState === TS_SHORT && curU > prevBbU) curU = prevBbU;

      // Money-risk adjustment (AFL formula)
      const range = curU - curL;
      const adj = ((moneyRisk - 1.0) / 2.0) * range;
      let slLong = curL - adj;
      let slShort = curU + adj;

      // Trail stops — never move against position
      if (bbState === TS_LONG && slLong < prevSlLong) slLong = prevSlLong;
      if (bbState === TS_SHORT && slShort > prevSlShort) slShort = prevSlShort;

      bbSignal[i] = bbState;
      bbStop[i] =
        bbState === TS_LONG ? slLong : bbState === TS_SHORT ? slShort : bbStop[i - 1];

      prevBbU = curU;
      prevBbL = curL;
      prevSlLong = slLong;
      prevSlShort = slShort;
    }

    // ════════════════════════════════════════════════════════════════
    // 3. Signal generation — both systems must agree (AFL loop)
    // ════════════════════════════════════════════════════════════════
    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));
    const showBackground = params.showBackground ?? "yes";
    const atr10 = computeATR(candles, 10);
    const markers: IndicatorMarker[] = [];
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number; type: "signal" | "tp1" | "tp2" | "sl"; position: "above" | "below" }[] = [];

    // Per-bar arrays (AFL-style forward-fill)
    const SIGNAL: TSignal[] = new Array(len).fill(TS_NONE);
    const ENTRY: number[] = new Array(len).fill(0);
    const SL: number[] = new Array(len).fill(0);
    const TP1: number[] = new Array(len).fill(0);
    const TP2: number[] = new Array(len).fill(0);
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");

    let prevSignalType: TSignal = TS_NONE;
    let lastSignalBar = -1;

    // Process only closed bars (AFL: lastClosedBar = BarCount-2 if forming)
    // In our system all candles are closed (backfill) except possibly the last
    const lastClosedBar = len - 2; // skip forming bar like AFL

    for (let i = 0; i <= lastClosedBar; i++) {
      const bb = bbSignal[i];
      const tm = tmSignal[i];

      if (bb === tm && bb !== TS_NONE) {
        const isNew = bb !== prevSignalType;

        if (isNew) {
          SIGNAL[i] = bb;
          ENTRY[i] = roundToTick(candles[i].close, tick);
          SL[i] = roundToTick(bbStop[i], tick);
          const risk = Math.abs(ENTRY[i] - SL[i]);

          if (bb === TS_LONG) {
            TP1[i] = roundToTick(ENTRY[i] + 0.618 * risk, tick);
            TP2[i] = roundToTick(ENTRY[i] + 1.618 * risk, tick);
          } else {
            TP1[i] = roundToTick(ENTRY[i] - 0.618 * risk, tick);
            TP2[i] = roundToTick(ENTRY[i] - 1.618 * risk, tick);
          }

          prevSignalType = bb;
          lastSignalBar = i;

          // Arrow markers (AFL: only for closed bars)
          if (bb === TS_LONG) {
            markers.push({
              time: candles[i].time,
              position: "belowBar",
              color: "#26a69a",
              shape: "arrowUp",
              text: "",
              size: 2,
            });
            arrowMarkers.push({ time: candles[i].time, direction: "long", price: candles[i].low, type: "signal", position: "below" });
          } else {
            markers.push({
              time: candles[i].time,
              position: "aboveBar",
              color: "#ef5350",
              shape: "arrowDown",
              text: "",
              size: 2,
            });
            arrowMarkers.push({ time: candles[i].time, direction: "short", price: candles[i].high, type: "signal", position: "above" });
          }
        } else {
          // Same signal — carry forward
          SIGNAL[i] = i > 0 ? SIGNAL[i - 1] : TS_NONE;
          ENTRY[i] = i > 0 ? ENTRY[i - 1] : 0;
          SL[i] = i > 0 ? SL[i - 1] : 0;
          TP1[i] = i > 0 ? TP1[i - 1] : 0;
          TP2[i] = i > 0 ? TP2[i - 1] : 0;
        }
      } else {
        // Disagreement — keep previous signal, forward-fill levels
        SIGNAL[i] = prevSignalType;
        if (i > 0) {
          ENTRY[i] = ENTRY[i - 1];
          SL[i] = SL[i - 1];
          TP1[i] = TP1[i - 1];
          TP2[i] = TP2[i - 1];
        }
      }
    }

    // Forward-fill to remaining bars (including forming bar) — AFL does this
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      for (let i = lastClosedBar + 1; i < len; i++) {
        SIGNAL[i] = SIGNAL[lastClosedBar];
        ENTRY[i] = ENTRY[lastClosedBar];
        SL[i] = SL[lastClosedBar];
        TP1[i] = TP1[lastClosedBar];
        TP2[i] = TP2[lastClosedBar];
      }
    }

    // Compute candle color state per bar
    for (let i = 0; i < len; i++) {
      if (SIGNAL[i] === TS_LONG) candleColorState[i] = "long";
      else if (SIGNAL[i] === TS_SHORT) candleColorState[i] = "short";
      else candleColorState[i] = "neutral";
    }

    // ════════════════════════════════════════════════════════════════
    // 4. Target / SL hit detection (AFL: HighestSince / LowestSince)
    // ════════════════════════════════════════════════════════════════
    let highSinceSignal = 0;
    let lowSinceSignal = Infinity;
    let status: AlphaTradeDashboard["status"] = "Active";
    let bookedPL = 0;
    let tp1Hit = false;
    let tp2Hit = false;
    let slHit = false;
    let tp1HitBar = -1;
    let tp2HitBar = -1;
    let slHitBar = -1;

    if (lastSignalBar >= 0) {
      const entryVal = ENTRY[lastSignalBar];
      const slVal = SL[lastSignalBar];
      const tp1Val = TP1[lastSignalBar];
      const tp2Val = TP2[lastSignalBar];
      const isLong = SIGNAL[lastSignalBar] === TS_LONG;

      // Scan bar-by-bar to find first hit bars (include live bar for real-time status)
      for (let i = lastSignalBar; i < len; i++) {
        highSinceSignal = Math.max(highSinceSignal, candles[i].high);
        lowSinceSignal = Math.min(lowSinceSignal, candles[i].low);

        if (isLong) {
          if (!slHit && candles[i].low <= slVal) { slHit = true; slHitBar = i; }
          if (!tp1Hit && candles[i].high >= tp1Val) { tp1Hit = true; tp1HitBar = i; }
          if (!tp2Hit && candles[i].high >= tp2Val) { tp2Hit = true; tp2HitBar = i; }
        } else {
          if (!slHit && candles[i].high >= slVal) { slHit = true; slHitBar = i; }
          if (!tp1Hit && candles[i].low <= tp1Val) { tp1Hit = true; tp1HitBar = i; }
          if (!tp2Hit && candles[i].low <= tp2Val) { tp2Hit = true; tp2HitBar = i; }
        }
      }

      if (tp2Hit) {
        status = "Target2 Hit";
        bookedPL = isLong ? tp2Val - entryVal : entryVal - tp2Val;
      } else if (tp1Hit) {
        status = "Target1 Hit";
        bookedPL = isLong ? tp1Val - entryVal : entryVal - tp1Val;
      } else if (slHit) {
        status = "Stop Loss Hit";
        bookedPL = isLong ? slVal - entryVal : entryVal - slVal;
      }

      // Push hit markers to arrowMarkers
      if (tp1HitBar >= 0) {
        arrowMarkers.push({ time: candles[tp1HitBar].time, direction: isLong ? "long" : "short", price: isLong ? candles[tp1HitBar].high : candles[tp1HitBar].low, type: "tp1", position: isLong ? "above" : "below" });
      }
      if (tp2HitBar >= 0) {
        arrowMarkers.push({ time: candles[tp2HitBar].time, direction: isLong ? "long" : "short", price: isLong ? candles[tp2HitBar].high : candles[tp2HitBar].low, type: "tp2", position: isLong ? "above" : "below" });
      }
      if (slHitBar >= 0) {
        arrowMarkers.push({ time: candles[slHitBar].time, direction: isLong ? "long" : "short", price: isLong ? candles[slHitBar].low : candles[slHitBar].high, type: "sl", position: isLong ? "below" : "above" });
      }
    }

    // ════════════════════════════════════════════════════════════════
    // 5. Dashboard metadata
    // ════════════════════════════════════════════════════════════════
    const uiBar = Math.max(0, len - 2); // closed bar for dashboard
    const currentPrice = candles[len - 1]?.close ?? 0;
    const currSignal = SIGNAL[uiBar];
    const currEntry = ENTRY[uiBar];
    const currSL = SL[uiBar];
    const currTP1 = TP1[uiBar];
    const currTP2 = TP2[uiBar];
    const currBB = bbSignal[uiBar];
    const currTM = tmSignal[uiBar];

    let dashSignal: AlphaTradeDashboard["signal"] = null;
    let dashTrend: AlphaTradeDashboard["trend"] = "NEUTRAL";
    let livePL = 0;

    if (currSignal !== TS_NONE && currEntry > 0) {
      if (currSignal === TS_LONG) {
        dashSignal = "BUY";
        dashTrend = "UP";
        livePL = currentPrice - currEntry;
      } else {
        dashSignal = "SELL";
        dashTrend = "DOWN";
        livePL = currEntry - currentPrice;
      }

      // Neutral when BB and TM disagree (AFL logic)
      if (currBB !== currTM || currBB === TS_NONE) {
        dashTrend = "NEUTRAL";
      }
    }

    const dashboard: AlphaTradeDashboard = {
      signal: dashSignal,
      trend: dashTrend,
      entry: currEntry > 0 ? currEntry : null,
      stopLoss: currSL > 0 ? currSL : null,
      target1: currTP1 > 0 ? currTP1 : null,
      target2: currTP2 > 0 ? currTP2 : null,
      currentPrice,
      livePL,
      bookedPL,
      status,
      tp1Hit,
      tp2Hit,
      slHit,
      candleState: candleColorState[uiBar],
    };

    // ════════════════════════════════════════════════════════════════
    // 6. Build level line series from signal bar onward + priceLines
    // ════════════════════════════════════════════════════════════════
    const entryData: { time: number; value: number }[] = [];
    const slData: { time: number; value: number }[] = [];
    const t1Data: { time: number; value: number }[] = [];
    const t2Data: { time: number; value: number }[] = [];
    const priceLines: IndicatorPriceLine[] = [];

    // Plot series intentionally left empty — priceLines handle all level drawing.
    if (lastSignalBar >= 0) {
      const fromTime = candles[lastSignalBar].time;

      // PriceLines that extend to right edge
      if (currEntry > 0) {
        priceLines.push({ id: "at_entry", price: currEntry, color: "#1E88E5", lineStyle: 2, lineWidth: lw, title: "Entry", fromTime });
        priceLines.push({ id: "at_sl", price: currSL, color: "#FF6D00", lineStyle: 2, lineWidth: lw, title: "SL", fromTime });
        priceLines.push({ id: "at_tp1", price: currTP1, color: "#00C853", lineStyle: 2, lineWidth: lw, title: "TP1", fromTime });
        priceLines.push({ id: "at_tp2", price: currTP2, color: "#00C853", lineStyle: 2, lineWidth: lw, title: "TP2", fromTime });
      }
    }

    // Candle colors: green for long, red for short, gray for neutral
    const candleColors = showBackground !== "no" ? candles.map((_c, i) => {
      const s = candleColorState[i];
      return { time: candles[i].time, color: s === "long" ? "#26a69a" : s === "short" ? "#ef5350" : "#787B86" };
    }) : [];

    return [
      { key: "entry", data: entryData, markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "stopLoss", data: slData },
      { key: "target1", data: t1Data },
      { key: "target2", data: t2Data },
    ];
  },
};
